import React, { useEffect, useMemo, useState } from "react"
import { Card } from "ui/Card"
import { Form, Input, Button, Divider, Flex, Select } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Icon } from "ui/Icon"
import PropTypes from "prop-types"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { colors } from "themes/variables"
import { Text } from "ui/Typography"
import { SelectTag } from "ui/SelectTag"
import { InfoTooltip } from "ui/Tooltip/InfoTooltip"

import { getMarketplaceTags } from "store/trips"
import { RequestMarketplaceTagModal } from "ui/RequestMarketplaceTagModal"
import { AddCategoryModal } from "../AddCategoryModal/AddCategoryModal"
import styles from "./details.module.css"

const { TextArea } = Input

/**
 * Wizard-local copy of ExperienceInfo.
 *
 * Key difference from the original: description is read directly from the AntD
 * form store via Form.useWatch instead of a local useState mirror. This prevents
 * the description from being wiped when the component unmounts and remounts
 * (the original's useState("") + useEffect would call form.setFieldsValue({ description: "" })
 * on every remount, erasing the preserved form value).
 */
export const ExperienceInfo = ({ form }) => {
  const [isShowAiModal, setIsShowAiModal] = useState(false)
  const [isShowRequestTagModal, setIsShowRequestTagModal] = useState(false)
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false)
  const [profileDescription, setProfileDescription] = useState("")
  const [t] = useTranslation()
  const dispatch = useDispatch()
  const { data: marketplaceTagsData, loading: isMarketplaceTagsLoading } = useSelector((state) => state.trips.marketplaceTags)
  const { data: categoriesData } = useSelector((state) => state.trips.categories)

  // Read description from form store reactively — no local state mirror that could wipe on remount
  const descriptionValue = Form.useWatch("description", form) || ""

  useEffect(() => {
    dispatch(getMarketplaceTags())
  }, [dispatch])

  const marketplaceTagOptions = useMemo(
    () => (marketplaceTagsData || []).map((tag) => ({ value: tag.id, label: tag.name })),
    [marketplaceTagsData]
  )

  const categoryOptions = useMemo(
    () => (categoriesData?.categories || []).map((cat) => ({ value: cat.id, label: cat.name })),
    [categoriesData]
  )

  // Receives { id, name } from AddCategoryModal after successful API save.
  // The reducer already appended it to categoriesData — just select it in the form.
  const handleAddCategory = ({ id }) => {
    form.setFieldValue("categories", id)
    setIsAddCategoryModalOpen(false)
  }

  const categoryDropdownRender = (menu) => (
    <>
      {menu}
      <Divider className={styles.categoryDivider} />
      <Flex>
        <Button
          type="link"
          icon={<PlusOutlined />}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setIsAddCategoryModalOpen(true)}
        >
          {"Add Category"}
        </Button>
      </Flex>
    </>
  )

  return (
    <>
      <Card title={t("experiences.details.experience_info")} className="mb-xl">
        <Form.Item
          name="title"
          label={t("experiences.details.labels.listing_title")}
          rules={[{ required: true, message: t("experiences.details.listing_title_error") }]}
        >
          <Input placeholder={t("experiences.details.listing_title_placeholder")} />
        </Form.Item>
        <Form.Item
          name="categories"
          label={t("experiences.details.labels.category")}
          rules={[{ required: true, message: t("experiences.details.errors.category_required") }]}
        >
          <Select
            showSearch
            placeholder={t("experiences.details.select_category_placeholder")}
            className={styles.select}
            options={categoryOptions}
            optionLabelProp="label"
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
            dropdownRender={categoryDropdownRender}
          />
        </Form.Item>
        <Form.Item
          name="marketplace_tags"
          label={
            <Flex gap={8} align="center">
              <span>{t("experiences.details.labels.marketplace_tags")}</span>
              <InfoTooltip title={t("experiences.details.tooltips.marketplace_tags")} />
            </Flex>
          }
          rules={[{ required: true, message: t("experiences.details.errors.marketplace_tags_required") }]}
        >
          <Select
            mode="multiple"
            maxCount={2}
            showSearch
            placeholder={t("experiences.details.placeholders.select_tags")}
            options={marketplaceTagOptions}
            loading={isMarketplaceTagsLoading}
            optionFilterProp="label"
            tagRender={SelectTag}
          />
        </Form.Item>
        <div className="mb-m mt-xs sm:text-center">
          <span className="text-sm">
            {t("experiences.details.no_tag_info")}{" "}
            <Button type="link" onClick={() => setIsShowRequestTagModal(true)} style={{ padding: 0 }}>
              {t("experiences.details.request_tag")}
            </Button>
          </span>
        </div>
        <Flex justify="space-between" align="center" className="w-full mb-xs">
          <Text className={styles.formItemLabel}>{t("global.description")} *</Text>
          <Button
            type="primary"
            icon={
              <Icon
                name="aiEnhancement"
                color={descriptionValue.length < 25 ? colors.textPrimary : colors.disabled}
                height={20}
                width={20}
              />
            }
            onClick={() => setIsShowAiModal(true)}
            disabled={descriptionValue.length < 25}
          >
            {t("client.enhance_with_ui")}
          </Button>
        </Flex>
        <Form.Item name="description" noStyle rules={[{ required: true, message: t("experiences.details.description_required_error") }]}>
          <TextArea placeholder={t("experiences.details.description_placeholder")} rows={4} />
        </Form.Item>
      </Card>
      <RequestMarketplaceTagModal
        isVisible={isShowRequestTagModal}
        onCancel={() => setIsShowRequestTagModal(false)}
      />
      <AddCategoryModal
        open={isAddCategoryModalOpen}
        onClose={() => setIsAddCategoryModalOpen(false)}
        onConfirm={handleAddCategory}
      />
      {isShowAiModal && (
        <AiEnhancementModal
          open={isShowAiModal}
          description={descriptionValue}
          setDescription={(val) => form.setFieldsValue({ description: val })}
          profileDescription={profileDescription}
          setProfileDescription={setProfileDescription}
          onOk={() => setIsShowAiModal(false)}
          onCancel={() => {
            setIsShowAiModal(false)
            setProfileDescription("")
          }}
          text={t("client.experience_content")}
          modalTitle={t("client.experiences_enhancer")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("client.refinement_tooltip")}
          improveWithAITooltipTitle={t("client.improve_with_ai_tooltip_for_experiences")}
        />
      )}
    </>
  )
}

ExperienceInfo.propTypes = {
  form: PropTypes.object.isRequired
}
